import React, { useState, useEffect } from 'react';
import { User } from '../../../types/user';
import UserService from '../../../services/users';
import UserFormModal from './UserFormModal';
import { useAuth } from '../../../contexts/AuthContext';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import ResultsCounter from '../../../components/Common/ResultsCounter';
import LoadMoreButton from '../../../components/Common/LoadMoreButton';
import './UserManagement.css';

const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filtros
  const [searchFilter, setSearchFilter] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  const [ativoFilter, setAtivoFilter] = useState<boolean | undefined>(undefined);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');

  // Hook de infinite scroll
  const {
    items: users,
    isLoading,
    totalItems,
    displayedItems,
    hasMore,
    loadMore,
    reset
  } = useInfiniteScroll<User>({
    fetchFunction: async (page, limit, filters) => {
      try {
        const response = await UserService.getUsers({
          page,
          limit,
          search: filters?.search || '',
          tipo: filters?.tipo || '',
          ativo: filters?.ativo
        });

        return {
          success: true,
          data: {
            items: response.usuarios,
            pagination: {
              total: response.pagination.total
            }
          }
        };
      } catch (err: any) {
        return {
          success: false,
          data: [],
          message: err.message || 'Erro ao carregar usuários'
        };
      }
    },
    itemsPerPage: 10,
    filters: { search: searchFilter, tipo: tipoFilter, ativo: ativoFilter },
    onError: (error) => {
      setError(error);
    }
  });

  // Limpar mensagens após 5 segundos
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Handlers
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchFilter(e.target.value);
  };

  const handleFilterTipo = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTipoFilter(e.target.value);
  };

  const handleFilterAtivo = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setAtivoFilter(value === '' ? undefined : value === 'true');
  };

  const handleCreateUser = () => {
    setModalMode('create');
    setSelectedUser(null);
    setShowModal(true);
  };

  const handleEditUser = (user: User) => {
    setModalMode('edit');
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleDeactivateUser = async (user: User) => {
    // Impedir auto-desativação
    if (currentUser && user.id === currentUser.id) {
      setError('Você não pode desativar sua própria conta');
      return;
    }

    if (!window.confirm(`Tem certeza que deseja desativar o usuário ${user.nome}?`)) {
      return;
    }

    try {
      await UserService.deactivateUser(user.id);
      setSuccess('Usuário desativado com sucesso');
      reset();
    } catch (err: any) {
      setError(err.message || 'Erro ao desativar usuário');
    }
  };

  const handleReactivateUser = async (user: User) => {
    if (!window.confirm(`Tem certeza que deseja reativar o usuário ${user.nome}?`)) {
      return;
    }

    try {
      await UserService.reactivateUser(user.id);
      setSuccess('Usuário reativado com sucesso');
      reset();
    } catch (err: any) {
      setError(err.message || 'Erro ao reativar usuário');
    }
  };

  const handleResetPassword = async (user: User) => {
    console.log('🔐 handleResetPassword iniciado para usuário:', user.id, user.nome);

    const enviarEmail = window.confirm(
      `Deseja resetar a senha do usuário ${user.nome}?\n\nClique em "OK" para enviar por email ou "Cancelar" para exibir a senha aqui.`
    );

    console.log('📧 Opção escolhida - enviarEmail:', enviarEmail);

    try {
      console.log('🚀 Chamando UserService.resetPassword...');
      const result = await UserService.resetPassword(user.id, enviarEmail);
      console.log('✅ Resposta recebida:', result);

      if (result.senha_temporaria) {
        alert(`Nova senha temporária para ${user.nome}:\n\n${result.senha_temporaria}\n\nGuarde esta senha, ela não será exibida novamente.`);
      } else {
        setSuccess('Email de redefinição de senha enviado com sucesso');
      }

      reset();
    } catch (err: any) {
      console.error('❌ Erro ao resetar senha:', err);
      setError(err.message || 'Erro ao resetar senha');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedUser(null);
  };

  const handleModalSuccess = (message: string) => {
    setSuccess(message);
    setShowModal(false);
    setSelectedUser(null);
    reset();
  };

  const getTipoLabel = (tipo: string) => {
    const labels: Record<string, string> = {
      admin: 'Administrador',
      supervisor: 'Supervisor',
      operador: 'Operador',
      conferente: 'Conferente'
    };
    return labels[tipo] || tipo;
  };

  const getTipoBadgeClass = (tipo: string) => {
    const classes: Record<string, string> = {
      admin: 'user-badge-admin',
      supervisor: 'user-badge-supervisor',
      operador: 'user-badge-operador',
      conferente: 'user-badge-conferente'
    };
    return classes[tipo] || '';
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Gerenciamento de Usuários</h1>
          <p className="page-subtitle">Gerencie usuários e permissões do sistema</p>
        </div>
        <button className="btn btn-primary" onClick={handleCreateUser}>
          <i className="fas fa-plus"></i> Novo Usuário
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <i className="fas fa-exclamation-circle"></i>
          {error}
          <button className="alert-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <i className="fas fa-check-circle"></i>
          {success}
          <button className="alert-close" onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      <div className="users-filters">
        <div className="filter-group">
          <i className="fas fa-search filter-icon"></i>
          <input
            type="text"
            className="form-control"
            placeholder="Buscar por nome ou email..."
            value={searchFilter}
            onChange={handleSearch}
          />
        </div>

        <div className="filter-group">
          <select
            className="form-control"
            value={tipoFilter}
            onChange={handleFilterTipo}
          >
            <option value="">Todos os tipos</option>
            <option value="admin">Administrador</option>
            <option value="supervisor">Supervisor</option>
            <option value="conferente">Conferente</option>
            <option value="operador">Operador</option>
          </select>
        </div>

        <div className="filter-group">
          <select
            className="form-control"
            value={ativoFilter === undefined ? '' : ativoFilter.toString()}
            onChange={handleFilterAtivo}
          >
            <option value="">Todos os status</option>
            <option value="true">Ativos</option>
            <option value="false">Inativos</option>
          </select>
        </div>
      </div>

      {isLoading && users.length === 0 ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Carregando usuários...</p>
        </div>
      ) : users.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-users"></i>
          <p>Nenhum usuário encontrado</p>
          <button className="btn btn-primary" onClick={handleCreateUser}>
            Criar Primeiro Usuário
          </button>
        </div>
      ) : (
        <>
          {/* Contador de resultados */}
          <ResultsCounter
            displayedItems={displayedItems}
            totalItems={totalItems}
            isLoading={isLoading}
            label="usuários"
          />

          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Cargo</th>
                  <th>Tipo</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} className={!user.ativo ? 'user-inactive' : ''}>
                    <td>
                      <div className="user-name-cell">
                        <strong>{user.nome}</strong>
                        {user.senha_temporaria && (
                          <span className="temp-password-badge" title="Senha temporária">
                            <i className="fas fa-key"></i>
                          </span>
                        )}
                        {user.primeiro_acesso && (
                          <span className="first-access-badge" title="Primeiro acesso">
                            <i className="fas fa-user-clock"></i>
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{user.email}</td>
                    <td>{user.cargo || '-'}</td>
                    <td>
                      <span className={`user-badge ${getTipoBadgeClass(user.tipo)}`}>
                        {getTipoLabel(user.tipo)}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${user.ativo ? 'status-active' : 'status-inactive'}`}>
                        {user.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEditUser(user)}
                          title="Editar usuário"
                        >
                          <i className="fas fa-edit"></i>
                        </button>

                        <button
                          className="btn-icon btn-reset"
                          onClick={() => handleResetPassword(user)}
                          title="Resetar senha"
                        >
                          <i className="fas fa-key"></i>
                        </button>

                        {user.ativo ? (
                          <button
                            className="btn-icon btn-deactivate"
                            onClick={() => handleDeactivateUser(user)}
                            title={currentUser?.id === user.id ? "Você não pode desativar sua própria conta" : "Desativar usuário"}
                            disabled={currentUser?.id === user.id}
                            style={currentUser?.id === user.id ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                          >
                            <i className="fas fa-ban"></i>
                          </button>
                        ) : (
                          <button
                            className="btn-icon btn-activate"
                            onClick={() => handleReactivateUser(user)}
                            title="Reativar usuário"
                          >
                            <i className="fas fa-check-circle"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Botão de carregar mais */}
          <LoadMoreButton
            onClick={loadMore}
            isLoading={isLoading}
            hasMore={hasMore}
          />
        </>
      )}

      {showModal && (
        <UserFormModal
          mode={modalMode}
          user={selectedUser}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default UserManagement;
